import type { Extensions, JSONContent } from "@tiptap/core";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import { FC, ReactNode, useEffect, useMemo } from "react";
import { MinimalTiptapToolbar } from "~/components/toolbars/MinimalTiptapToolbar";
import { ToolbarProvider } from "~/components/toolbars/toolbar-provider";
import { cn } from "~/lib/utils";
import { markdownToHtmlSync } from "~/utils/htmlToMarkdown";

interface NoteEditorProps {
  content: string;
  onChange: (content: string) => void;
  jsonContent?: JSONContent;
  onJsonChange?: (content: JSONContent) => void;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  readOnly?: boolean;
  activeTtsIdx?: number | null;
  activeTtsLen?: number | null;
  textSize?: "sm" | "base" | "lg" | "xl" | "2xl";
  textColor?: string;
  textSizeMobile?: "sm" | "base" | "lg" | "xl" | "2xl";
  showToolbar?: boolean;
  stickyToolbar?: boolean;
  enableImages?: boolean;
  organizationId?: string;
  projectId?: string;
  imageInputRef?: React.RefObject<HTMLInputElement | null>;
  onImageUploadPendingChange?: (isPending: boolean) => void;
  extraExtensions?: Extensions;
  children?: (editor: Editor) => ReactNode;
}

const TTS_HIGHLIGHT_PLUGIN_KEY = new PluginKey("ttsHighlight");

function createTtsHighlightPlugin(activeIdx: number, activeLen: number) {
  return new Plugin({
    key: TTS_HIGHLIGHT_PLUGIN_KEY,
    props: {
      decorations(state) {
        const { doc } = state;
        if (activeLen <= 0) return null;

        let offset = 0;
        let fromPos: number | null = null;
        let toPos: number | null = null;

        doc.descendants((node, pos) => {
          if (!node.isText) {
            return true;
          }
          const textLen = node.text ? node.text.length : 0;
          const start = offset;
          const end = offset + textLen;

          const a0 = activeIdx;
          const a1 = activeIdx + activeLen;

          const overlapStart = Math.max(start, a0);
          const overlapEnd = Math.min(end, a1);

          if (overlapStart < overlapEnd) {
            const relFrom = overlapStart - start;
            const relTo = overlapEnd - start;
            fromPos = pos + relFrom;
            toPos = pos + relTo;
            return false;
          }

          offset = end;
          return true;
        });

        if (fromPos != null && toPos != null && toPos > fromPos) {
          const deco = Decoration.inline(fromPos, toPos, {
            class: "underline decoration-dotted",
          });
          return DecorationSet.create(doc, [deco]);
        }
        return null;
      },
    },
  });
}

export const NoteEditor: FC<NoteEditorProps> = ({
  content,
  onChange,
  jsonContent,
  onJsonChange,
  placeholder = "Start writing your note...",
  className,
  contentClassName,
  readOnly = false,
  activeTtsIdx = null,
  activeTtsLen = null,
  textSize = "base",
  textColor,
  textSizeMobile = "base",
  showToolbar = false,
  stickyToolbar = false,
  enableImages = false,
  organizationId,
  projectId,
  imageInputRef,
  onImageUploadPendingChange,
  extraExtensions,
  children,
}) => {
  // Convert markdown to HTML for TipTap editor
  const htmlContent = useMemo(() => {
    if (!content) return content;
    // If content starts with a block-level HTML tag (e.g. from TipTap's
    // getHTML()), treat it as already-HTML. Markdown that contains inline
    // HTML elements like <a> source-map links must still be converted.
    const trimmed = content.trim();
    if (
      /^<(p|h[1-6]|ul|ol|li|blockquote|div|table|pre|hr|img)\b/i.test(trimmed)
    )
      return content;
    // Convert markdown to HTML
    return markdownToHtmlSync(content);
  }, [content]);

  const editorContent = jsonContent ?? htmlContent;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Placeholder.configure({
        placeholder,
      }),
      ...(enableImages
        ? [
            Image.configure({
              inline: false,
              allowBase64: true,
              HTMLAttributes: {
                class: "rounded-lg max-w-full h-auto",
              },
            }),
          ]
        : []),
      ...(extraExtensions ?? []),
    ],
    content: editorContent,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (onJsonChange) {
        onJsonChange(editor.getJSON());
      } else {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm dark:prose-invert max-w-none focus:outline-hidden whitespace-pre-wrap",
          {
            "sm:text-sm": textSize === "sm",
            "sm:text-base": textSize === "base",
            "sm:text-lg": textSize === "lg",
            "sm:text-xl": textSize === "xl",
            "sm:text-2xl": textSize === "2xl",
          },
          {
            "text-sm": textSizeMobile === "sm",
            "text-base": textSizeMobile === "base",
            "text-lg": textSizeMobile === "lg",
            "text-xl": textSizeMobile === "xl",
            "text-2xl": textSizeMobile === "2xl",
          }
        ),
        ...(textColor && { style: `color: ${textColor}` }),
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (jsonContent) {
      if (JSON.stringify(jsonContent) !== JSON.stringify(editor.getJSON())) {
        editor.commands.setContent(jsonContent, { emitUpdate: false });
      }
      return;
    }
    if (htmlContent !== editor.getHTML()) {
      editor.commands.setContent(htmlContent, { emitUpdate: false });
    }
  }, [htmlContent, jsonContent, editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useEffect(() => {
    if (!editor) return;

    const view: EditorView = editor.view;
    const prev = TTS_HIGHLIGHT_PLUGIN_KEY.get(view.state);
    if (prev) {
    }

    const hasActive =
      typeof activeTtsIdx === "number" &&
      typeof activeTtsLen === "number" &&
      activeTtsIdx >= 0 &&
      activeTtsLen > 0;

    const plugin = hasActive
      ? createTtsHighlightPlugin(activeTtsIdx, activeTtsLen)
      : new Plugin({
          key: TTS_HIGHLIGHT_PLUGIN_KEY,
          props: {
            decorations() {
              return null;
            },
          },
        });

    editor.registerPlugin(plugin);
    view.dispatch(view.state.tr.setMeta("addToHistory", false));

    return () => {
      editor.unregisterPlugin(TTS_HIGHLIGHT_PLUGIN_KEY);
      view.dispatch(view.state.tr.setMeta("addToHistory", false));
    };
  }, [editor, activeTtsIdx, activeTtsLen]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("relative", className)}>
      <ToolbarProvider editor={editor}>
        <div className={cn(stickyToolbar && "overflow-y-auto max-h-[70vh]")}>
          {showToolbar && (
            <MinimalTiptapToolbar
              enableImages={enableImages}
              organizationId={organizationId}
              projectId={projectId}
              imageInputRef={imageInputRef}
              onImageUploadPendingChange={onImageUploadPendingChange}
              sticky={stickyToolbar}
            />
          )}
          <div
            onClick={() => {
              editor?.chain().focus().run();
            }}
            className={cn(
              "cursor-text min-h-72 bg-background p-3",
              contentClassName
            )}
          >
            <EditorContent className="outline-hidden" editor={editor} />
          </div>
        </div>
        {children?.(editor)}
      </ToolbarProvider>
    </div>
  );
};
